// Decodes the Qundis Ei6500-oms "Head Status" M-Bus datapoint (DIF 84h, DIFE 40h, VIF FFh, VIFE 2Ch):
// a 32-bit unsigned integer where each bit/bit-group reports a device health check.
// Bit layout cross-checked against Qundis's own reference spreadsheet
// (Fehlercodes-Qundis_RWM_2023-03-15.xlsx, sheet "Ei6500-OMS") and their error-code PDF
// spec (Table 21/22). Unrelated to the older Q smoke 5.5 "Fehlercode" 16-bit bitmask
// (Table 15) — do not conflate if that device is ever added.
//
// Severity per field (error/warning/status) and message wording come from
// Ei6500_OMS_Statuscodes_detailliert.xlsx (provided 2026-08-24), the first source that
// actually classifies each bit rather than just describing it — neither of the two
// documents above assign severity for this device.

const HEAD_STATUS_FIELDS = {
  DUST_LEVEL: { start: 0, bits: 5 },
  SOUNDER_FAULT: { start: 5, bits: 1 },
  TAMPER: { start: 6, bits: 1 },
  END_OF_LIFE_REACHED: { start: 7, bits: 1 },
  BATTERY_LEVEL: { start: 8, bits: 4 },
  WEAK_BATTERY: { start: 12, bits: 1 },
  ALARM_SENSOR_FAULT: { start: 13, bits: 1 },
  OBSTACLE_DETECTION_FAULT: { start: 14, bits: 1 },
  END_OF_LIFE_WITHIN_12_MONTHS: { start: 15, bits: 1 },
  SEODS_INSTALLATION_COMPLETE: { start: 16, bits: 1 },
  ENVIRONMENT_CHANGED: { start: 17, bits: 1 },
  COMMS_ERROR: { start: 18, bits: 1 },
  OBSTACLE_CHECK_BLOCKED: { start: 19, bits: 1 },
  OBSTACLE_DISTANCE: { start: 20, bits: 3 },
  // bit 23 reserved
  OBSTACLE_DETECTED: { start: 24, bits: 1 },
  COVER_DETECTED: { start: 25, bits: 1 },
  // bits 26-31 reserved
};

const OBSTACLE_DISTANCE_LABELS = [
  'Unbekannt',
  'Kein Hindernis erkannt',
  '0,45–0,56 m',
  '0,38–0,53 m',
  '0,33–0,48 m',
  '0,28–0,40 m',
  '0,20–0,33 m',
  '0,00–0,25 m',
];

// >>> converts to uint32 first, so this is safe even for the top bits (24-31) of a raw
// value up to 4294967295, unlike plain `&`/`<<` which operate on signed 32-bit ints.
function getBits(value, start, numBits) {
  const mask = (1 << numBits) - 1;
  return (value >>> start) & mask;
}

function getDustLevelLabel(dustLevel) {
  if (dustLevel === 0) return 'Staubfrei (Stufe 0/31)';
  if (dustLevel >= 27) return `Empfohlene Reinigung (Stufe ${dustLevel}/31)`;
  return `Staub vorhanden (Stufe ${dustLevel}/31)`;
}

export function decodeBatteryVoltage(batteryLevelRaw) {
  return Math.round((batteryLevelRaw * 0.05 + 2.25) * 100) / 100;
}

export function decodeHeadStatus(rawValue) {
  const dustLevel = getBits(rawValue, HEAD_STATUS_FIELDS.DUST_LEVEL.start, HEAD_STATUS_FIELDS.DUST_LEVEL.bits);
  const batteryLevelRaw = getBits(rawValue, HEAD_STATUS_FIELDS.BATTERY_LEVEL.start, HEAD_STATUS_FIELDS.BATTERY_LEVEL.bits);
  const obstacleDistanceCode = getBits(rawValue, HEAD_STATUS_FIELDS.OBSTACLE_DISTANCE.start, HEAD_STATUS_FIELDS.OBSTACLE_DISTANCE.bits);

  return {
    rawValue,
    dustLevel,
    dustLevelLabel: getDustLevelLabel(dustLevel),
    sounderFault: !!getBits(rawValue, HEAD_STATUS_FIELDS.SOUNDER_FAULT.start, 1),
    tamper: !!getBits(rawValue, HEAD_STATUS_FIELDS.TAMPER.start, 1),
    endOfLifeReached: !!getBits(rawValue, HEAD_STATUS_FIELDS.END_OF_LIFE_REACHED.start, 1),
    batteryLevelRaw,
    batteryVoltage: decodeBatteryVoltage(batteryLevelRaw),
    weakBattery: !!getBits(rawValue, HEAD_STATUS_FIELDS.WEAK_BATTERY.start, 1),
    alarmSensorFault: !!getBits(rawValue, HEAD_STATUS_FIELDS.ALARM_SENSOR_FAULT.start, 1),
    obstacleDetectionFault: !!getBits(rawValue, HEAD_STATUS_FIELDS.OBSTACLE_DETECTION_FAULT.start, 1),
    endOfLifeWithin12Months: !!getBits(rawValue, HEAD_STATUS_FIELDS.END_OF_LIFE_WITHIN_12_MONTHS.start, 1),
    seodsInstallationComplete: !!getBits(rawValue, HEAD_STATUS_FIELDS.SEODS_INSTALLATION_COMPLETE.start, 1),
    environmentChanged: !!getBits(rawValue, HEAD_STATUS_FIELDS.ENVIRONMENT_CHANGED.start, 1),
    commsError: !!getBits(rawValue, HEAD_STATUS_FIELDS.COMMS_ERROR.start, 1),
    obstacleCheckBlocked: !!getBits(rawValue, HEAD_STATUS_FIELDS.OBSTACLE_CHECK_BLOCKED.start, 1),
    obstacleDistanceCode,
    obstacleDistanceLabel: OBSTACLE_DISTANCE_LABELS[obstacleDistanceCode],
    obstacleDetected: !!getBits(rawValue, HEAD_STATUS_FIELDS.OBSTACLE_DETECTED.start, 1),
    coverDetected: !!getBits(rawValue, HEAD_STATUS_FIELDS.COVER_DETECTED.start, 1),
  };
}

// Single source of truth for both the error/warning roll-up and the per-check message
// list below. Field order and severity/wording match Ei6500_OMS_Statuscodes_detailliert.xlsx
// exactly. `invert` marks the one field (SEODS install) where the documented "bad" state
// is decoded[field] === false rather than true — every other field is 1=bad, 0=ok.
const BINARY_CHECKS = [
  { field: 'tamper', severity: 'error', okMessage: 'Keine Geräte-Manipulation', badMessage: 'Geräte-Manipulation erkannt' },
  { field: 'sounderFault', severity: 'error', okMessage: 'Kein Signalgeber-Fehler', badMessage: 'Fehler Signalgeber' },
  { field: 'endOfLifeReached', severity: 'error', okMessage: 'Kein Ende der Lebensdauer erreicht', badMessage: 'Ende der Lebensdauer erreicht' },
  { field: 'alarmSensorFault', severity: 'error', okMessage: 'Kein Alarm Sensorfehler', badMessage: 'Alarm Sensorfehler' },
  { field: 'obstacleDetectionFault', severity: 'error', okMessage: 'Kein Fehler bei der Hinderniserkennung', badMessage: 'Fehler bei der Hinderniserkennung' },
  { field: 'commsError', severity: 'error', okMessage: 'Kein Fehler in der Kommunikation', badMessage: 'Fehler in der Kommunikation' },
  { field: 'obstacleCheckBlocked', severity: 'error', okMessage: 'Kein Hinderniserkennungsproblem', badMessage: 'Hinderniserkennung nicht möglich' },
  { field: 'coverDetected', severity: 'error', okMessage: 'Keine Abdeckung des Gerätes erkannt', badMessage: 'Geräte-Abdeckung wurde erkannt' },
  { field: 'seodsInstallationComplete', severity: 'error', invert: true, okMessage: 'Installation abgeschlossen', badMessage: 'Installation nicht abgeschlossen' },
  { field: 'weakBattery', severity: 'warning', okMessage: 'Kein Fehler bei schwacher Batterie', badMessage: 'Schwache Batterie' },
  { field: 'endOfLifeWithin12Months', severity: 'warning', okMessage: 'Kein Ende der Lebensdauer < 12 Monate', badMessage: 'Ende der Lebensdauer < 12 Monate' },
  { field: 'obstacleDetected', severity: 'warning', okMessage: 'Kein Hindernis erkannt', badMessage: 'Hindernis erkannt' },
  { field: 'environmentChanged', severity: 'warning', okMessage: 'Umgebung unverändert seit letzter Installation', badMessage: 'Umgebung hat sich seit der letzten Installation geändert' },
];

const isTriggered = (decoded, { field, invert }) => (invert ? !decoded[field] : !!decoded[field]);

export function getHeadStatusSeverity(decoded) {
  const errorFields = [];
  const warningFields = [];

  BINARY_CHECKS.forEach((check) => {
    if (!isTriggered(decoded, check)) return;
    if (check.severity === 'error') errorFields.push(check.field);
    else if (check.severity === 'warning') warningFields.push(check.field);
  });
  if (decoded.dustLevel >= 27) warningFields.push('dustLevel');

  return {
    hasError: errorFields.length > 0,
    hasWarning: warningFields.length > 0,
    errorFields,
    warningFields,
  };
}

// Compact German label per check, for badge/chip UI where getGermanStatusMessages's
// full sentence (e.g. "Manipulation erkannt (Gerät von der Montageplatte entfernt)") is too long.
export const FIELD_SHORT_LABELS = {
  tamper: 'Manipulation',
  sounderFault: 'Signalgeber',
  alarmSensorFault: 'Alarmsensor',
  obstacleDetectionFault: 'Hinderniserkennung',
  commsError: 'Kommunikation',
  endOfLifeReached: 'Lebensdauer',
  weakBattery: 'Batterie',
  endOfLifeWithin12Months: 'Lebensdauer (<12 Mon.)',
  obstacleDetected: 'Hindernis',
  coverDetected: 'Abdeckung',
  obstacleCheckBlocked: 'Hindernisprüfung',
  environmentChanged: 'Umgebung',
  dustLevel: 'Staub',
  batteryVoltage: 'Batteriespannung',
  obstacleDistance: 'Hindernisabstand',
  seodsInstallationComplete: 'Installation',
};

export function getGermanStatusMessages(decoded) {
  const messages = BINARY_CHECKS.map((check) => {
    const triggered = isTriggered(decoded, check);
    return {
      field: check.field,
      message: triggered ? check.badMessage : check.okMessage,
      severity: triggered ? check.severity : 'ok',
    };
  });

  // Dust, battery voltage, and obstacle distance are "Status" tier in the source doc — always
  // a plain descriptive reading, not itself an error/warning — except dust >=27, the one
  // documented Warning threshold for this field.
  messages.push({
    field: 'dustLevel',
    message: decoded.dustLevelLabel,
    severity: decoded.dustLevel >= 27 ? 'warning' : 'ok',
  });
  messages.push({
    field: 'batteryVoltage',
    message: `Stufe ${decoded.batteryLevelRaw}/15: ${decoded.batteryVoltage.toFixed(2).replace('.', ',')}V`,
    severity: 'ok',
  });
  messages.push({
    field: 'obstacleDistance',
    message: decoded.obstacleDistanceLabel,
    severity: 'ok',
  });

  return messages;
}
